import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    Receipt, FunnelSimple, ArrowsClockwise, CheckCircle, Hourglass,
    DownloadSimple, ShieldCheck, FileText, ListChecks,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { formatINR, formatDate } from "../../lib/format";
import { sortByDate, SortableHeader } from "../../lib/dateSort";
import PageHeader from "../../components/layout/PageHeader";
import EmptyState from "../../components/EmptyState";
import RoleGuard from "../../components/RoleGuard";
import DateFilterButton from "../../components/DateFilterButton";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import { cn as classJoin } from "../../lib/utils";

const FILING_TONE = {
    "Filed": "bg-emerald-50 text-emerald-700 border-emerald-200",
    "Not Filed": "bg-rose-50 text-rose-700 border-rose-200",
};

const EMPTY_FILTERS = { client_id: "", date_from: "", date_to: "", filing_status: "", gst_type: "" };

export default function GstReport() {
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [data, setData] = useState(null);
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(new Set());
    const [bulkBusy, setBulkBusy] = useState(false);
    const [bulkOpen, setBulkOpen] = useState(false);
    const [bulkAction, setBulkAction] = useState("Filed");
    const [bulkDate, setBulkDate] = useState(new Date().toISOString().slice(0, 10));
    // Amendment 5 (UAT-002, 2026-02-12) — client-side sort on Invoice Date.
    // `null` => preserve backend default (invoice_date DESC). Operator can
    // toggle via the sortable column header in the detail table.
    const [sortState, setSortState] = useState(null);

    const load = async () => {
        try {
            const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
            const [r, c] = await Promise.all([
                api.get("/billing/reports/compliance/gst", { params }),
                clients.length ? Promise.resolve({ data: { items: clients } }) : api.get("/billing/client/list"),
            ]);
            setData(r.data);
            if (c.data?.items && !clients.length) setClients(c.data.items);
            setSelected(new Set());
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load GST report"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

    const toggle = (id) => {
        const next = new Set(selected);
        next.has(id) ? next.delete(id) : next.add(id);
        setSelected(next);
    };
    // Amendment 5 — sorted view of the detail items (no-op when sortState is null).
    const sortedItems = useMemo(
        () => sortByDate(data?.items || [], sortState),
        [data, sortState],
    );
    const toggleAll = () => {
        if (selected.size === data.items.length) setSelected(new Set());
        else setSelected(new Set(data.items.map((r) => r.id)));
    };

    const updateFiling = async (id, status) => {
        try {
            await api.patch(`/billing/invoice/${id}/gst-filing`, { gst_filing_status: status });
            toast.success(status === "Filed" ? "Marked Filed" : "Marked Not Filed");
            await load();
        } catch (err) {
            toast.error(formatApiError(err, "Failed"));
        }
    };

    const bulkApply = async () => {
        if (selected.size === 0) {
            toast.error("Select at least one invoice");
            return;
        }
        setBulkBusy(true);
        try {
            const { data: r } = await api.post("/billing/invoice/gst-filing/bulk", {
                invoice_ids: Array.from(selected),
                gst_filing_status: bulkAction,
                gst_filing_date: bulkAction === "Filed" ? new Date(bulkDate).toISOString() : null,
            });
            toast.success(`${r.updated} invoice${r.updated !== 1 ? "s" : ""} marked ${bulkAction}`);
            setBulkOpen(false);
            await load();
        } catch (err) {
            toast.error(formatApiError(err, "Bulk update failed"));
        } finally {
            setBulkBusy(false);
        }
    };

    const csv = () => {
        if (!data?.items?.length) return;
        const cols = ["Invoice No", "Invoice Date", "Client", "Client GSTIN", "Client State", "Taxable Value", "CGST", "SGST", "IGST", "Total Tax", "Invoice Amount", "GST Type", "Filing Status", "Filing Date"];
        const rows = data.items.map((r) => [
            r.invoice_number, r.invoice_date, r.client_name, r.client_gstin, r.client_state,
            r.taxable_value, r.cgst, r.sgst, r.igst, r.total_tax, r.invoice_amount, r.gst_type,
            r.filing_status, r.filing_date || "",
        ].map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","));
        const blob = new Blob([cols.join(",") + "\n" + rows.join("\n")], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `gst-report-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const reset = () => setFilters(EMPTY_FILTERS);

    const allSelected = useMemo(() => data?.items?.length > 0 && selected.size === data.items.length, [data, selected]);

    if (loading && !data) return <div data-testid="gst-page-loading"><PageHeader eyebrow="Compliance · GST" title="Loading…" /></div>;
    if (!data) return null;

    return (
        <div data-testid="gst-report-page">
            <PageHeader
                eyebrow="Compliance"
                title={
                    <span className="flex items-center gap-3">
                        <Receipt size={22} weight="duotone" className="text-indigo-600" />
                        GST Report
                    </span>
                }
                description="Per-invoice GST register. Mark invoices as Filed once they're submitted to GSTR-1. GST is invoice-based — payments don't change filing status."
                actions={
                    <div className="flex gap-2">
                        <RoleGuard permission="invoice:write">
                            <Button variant="outline" size="sm" onClick={() => setBulkOpen(true)} disabled={selected.size === 0} className="gap-1.5" data-testid="gst-bulk-btn">
                                <ListChecks size={13} /> Bulk · {selected.size} selected
                            </Button>
                        </RoleGuard>
                        <Button variant="outline" size="sm" onClick={csv} disabled={!data.items.length} className="gap-1.5" data-testid="gst-export-csv-btn">
                            <DownloadSimple size={13} /> Export CSV
                        </Button>
                    </div>
                }
            />

            <div className="px-6 sm:px-8 py-8 space-y-6">
                {/* KPI strip */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="gst-kpis">
                    <Kpi label="Invoices" value={data.total} icon={<Receipt size={11} />} testid="gst-kpi-invoices" />
                    <Kpi label="Taxable value" value={formatINR(data.totals.taxable_value)} icon={<FileText size={11} />} testid="gst-kpi-taxable" />
                    <Kpi label="Total GST" value={formatINR(data.totals.total_tax)} sub={`CGST ${formatINR(data.totals.cgst)} · SGST ${formatINR(data.totals.sgst)} · IGST ${formatINR(data.totals.igst)}`} icon={<ShieldCheck size={11} />} tone="indigo" testid="gst-kpi-total-tax" />
                    <Kpi
                        label="Filed"
                        value={`${data.counts_by_filing.Filed} of ${data.total}`}
                        sub={`${data.counts_by_filing["Not Filed"]} pending`}
                        icon={<CheckCircle size={11} weight="fill" />}
                        tone={data.counts_by_filing.Filed === data.total ? "emerald" : data.counts_by_filing.Filed > 0 ? "amber" : "rose"}
                        testid="gst-kpi-filed"
                    />
                </div>

                {/* Monthly summary */}
                {data.by_month?.length > 0 && (
                    <div className="reedos-card overflow-hidden" data-testid="gst-by-month">
                        <div className="px-5 py-3 border-b border-slate-200">
                            <div className="reedos-eyebrow">Monthly summary (GSTR-1 friendly)</div>
                            <div className="text-[11px] text-slate-500 mt-0.5">Last {data.by_month.length} months · invoice count + taxable + total tax + filing progress</div>
                        </div>
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 border-b border-slate-200">
                                <tr className="text-left">
                                    <th className="px-3 py-2 reedos-eyebrow text-[9px]">Month</th>
                                    <th className="px-3 py-2 reedos-eyebrow text-[9px] text-right">Invoices</th>
                                    <th className="px-3 py-2 reedos-eyebrow text-[9px] text-right">Taxable</th>
                                    <th className="px-3 py-2 reedos-eyebrow text-[9px] text-right">CGST</th>
                                    <th className="px-3 py-2 reedos-eyebrow text-[9px] text-right">SGST</th>
                                    <th className="px-3 py-2 reedos-eyebrow text-[9px] text-right">IGST</th>
                                    <th className="px-3 py-2 reedos-eyebrow text-[9px] text-right">Total tax</th>
                                    <th className="px-3 py-2 reedos-eyebrow text-[9px] text-right">Invoice value</th>
                                    <th className="px-3 py-2 reedos-eyebrow text-[9px] text-right">Filed / NF</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.by_month.map((m) => (
                                    <tr key={m.month} className="border-t border-slate-100 reedos-row-hover" data-testid={`gst-month-row-${m.month}`}>
                                        <td className="px-3 py-2 text-xs font-mono font-semibold">{m.month}</td>
                                        <td className="px-3 py-2 text-xs text-right reedos-data">{m.invoice_count}</td>
                                        <td className="px-3 py-2 text-xs text-right reedos-data">{formatINR(m.taxable_value)}</td>
                                        <td className="px-3 py-2 text-xs text-right reedos-data text-slate-600">{m.cgst ? formatINR(m.cgst) : <span className="text-slate-300">—</span>}</td>
                                        <td className="px-3 py-2 text-xs text-right reedos-data text-slate-600">{m.sgst ? formatINR(m.sgst) : <span className="text-slate-300">—</span>}</td>
                                        <td className="px-3 py-2 text-xs text-right reedos-data text-slate-600">{m.igst ? formatINR(m.igst) : <span className="text-slate-300">—</span>}</td>
                                        <td className="px-3 py-2 text-xs text-right reedos-data font-semibold text-indigo-700">{formatINR(m.total_tax)}</td>
                                        <td className="px-3 py-2 text-xs text-right reedos-data text-slate-700">{formatINR(m.invoice_amount || 0)}</td>
                                        <td className="px-3 py-2 text-xs text-right">
                                            <span className="text-emerald-700 font-semibold">{m.filed}</span>
                                            <span className="text-slate-300 mx-1">/</span>
                                            <span className="text-rose-700 font-semibold">{m.not_filed}</span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Filters */}
                <div className="reedos-card p-4" data-testid="gst-filters">
                    <div className="flex items-center justify-between mb-3">
                        <div className="reedos-eyebrow flex items-center gap-1.5"><FunnelSimple size={11} /> Filters</div>
                        <Button variant="ghost" size="sm" onClick={reset} className="text-[11px] gap-1 text-slate-500 hover:text-slate-900" data-testid="gst-filters-reset">
                            <ArrowsClockwise size={11} /> Reset
                        </Button>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                        <DateFilterButton label="From · invoice date" value={filters.date_from} onChange={(v) => setFilters({ ...filters, date_from: v })} testid="gst-filter-date-from" />
                        <DateFilterButton label="To · invoice date" value={filters.date_to} onChange={(v) => setFilters({ ...filters, date_to: v })} testid="gst-filter-date-to" />
                        <FilterSelect label="Client" value={filters.client_id} onChange={(v) => setFilters({ ...filters, client_id: v })} options={[{ value: "", label: "All clients" }, ...clients.map((c) => ({ value: c.id, label: c.name }))]} testid="gst-filter-client" />
                        <FilterSelect label="GST Type" value={filters.gst_type} onChange={(v) => setFilters({ ...filters, gst_type: v })} options={[{ value: "", label: "All" }, { value: "CGST_SGST", label: "CGST + SGST" }, { value: "IGST", label: "IGST" }]} testid="gst-filter-gst-type" />
                        <FilterSelect label="Filing" value={filters.filing_status} onChange={(v) => setFilters({ ...filters, filing_status: v })} options={[{ value: "", label: "All" }, { value: "Filed", label: "Filed" }, { value: "Not Filed", label: "Not Filed" }]} testid="gst-filter-filing" />
                    </div>
                </div>

                {/* Table */}
                <div className="reedos-card overflow-hidden" data-testid="gst-table">
                    {data.items.length === 0 ? (
                        <EmptyState icon={<Receipt size={36} weight="thin" />} title="No invoices match the filter" />
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-slate-50 border-b border-slate-200">
                                    <tr className="text-left">
                                        <th className="px-3 py-2">
                                            <input type="checkbox" checked={allSelected} onChange={toggleAll} className="w-3.5 h-3.5 accent-slate-900" data-testid="gst-select-all" />
                                        </th>
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px]">Invoice</th>
                                        <SortableHeader
                                            field="invoice_date"
                                            label="Invoice Date"
                                            sortState={sortState}
                                            onSort={setSortState}
                                            testid="gst-sort-invoice-date"
                                        />
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px]">Client</th>
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px] text-right">Taxable</th>
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px] text-right">CGST</th>
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px] text-right">SGST</th>
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px] text-right">IGST</th>
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px] text-right">Total</th>
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px]">Type</th>
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px]">Filing</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedItems.map((r) => (
                                        <tr key={r.id} className="border-t border-slate-100 reedos-row-hover" data-testid={`gst-row-${r.id}`}>
                                            <td className="px-3 py-2">
                                                <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} className="w-3.5 h-3.5 accent-slate-900" data-testid={`gst-row-select-${r.id}`} />
                                            </td>
                                            <td className="px-4 py-2 text-xs">
                                                <Link to={`/invoices/${r.id}`} className="font-mono font-semibold text-slate-900 hover:underline">{r.invoice_number}</Link>
                                                <div className="text-[10px] text-slate-500">{formatDate(r.invoice_date)}</div>
                                            </td>
                                            <td className="px-4 py-2 text-xs text-slate-700 reedos-data" data-testid={`gst-invoice-date-${r.id}`}>{formatDate(r.invoice_date)}</td>
                                            <td className="px-4 py-2 text-xs">
                                                <Link to={`/clients/${r.client_id}/ledger`} className="hover:underline text-slate-900 font-medium">{r.client_name}</Link>
                                                <div className="text-[10px] text-slate-500">{r.client_state} · {r.client_gstin || "no GSTIN"}</div>
                                            </td>
                                            <td className="px-4 py-2 text-xs text-right reedos-data">{formatINR(r.taxable_value)}</td>
                                            <td className="px-4 py-2 text-xs text-right reedos-data text-slate-700">{r.cgst > 0 ? formatINR(r.cgst) : "—"}</td>
                                            <td className="px-4 py-2 text-xs text-right reedos-data text-slate-700">{r.sgst > 0 ? formatINR(r.sgst) : "—"}</td>
                                            <td className="px-4 py-2 text-xs text-right reedos-data text-slate-700">{r.igst > 0 ? formatINR(r.igst) : "—"}</td>
                                            <td className="px-4 py-2 text-xs text-right reedos-data font-bold text-indigo-700">{formatINR(r.total_tax)}</td>
                                            <td className="px-4 py-2 text-[10px]">
                                                <span className={classJoin("inline-flex rounded px-1.5 py-0.5 font-bold border",
                                                    r.gst_type === "IGST" ? "bg-violet-50 text-violet-700 border-violet-200" : "bg-blue-50 text-blue-700 border-blue-200")}>
                                                    {r.gst_type === "IGST" ? "IGST" : "CGST+SGST"}
                                                </span>
                                            </td>
                                            <td className="px-4 py-2 text-xs">
                                                <RoleGuard
                                                    permission="invoice:write"
                                                    fallback={
                                                        <span className={classJoin("inline-flex items-center gap-1 rounded px-2 py-0.5 text-[10px] font-semibold border", FILING_TONE[r.filing_status])}>
                                                            {r.filing_status === "Filed" ? <CheckCircle size={11} weight="fill" /> : <Hourglass size={11} weight="fill" />}
                                                            {r.filing_status}
                                                        </span>
                                                    }
                                                >
                                                    <button
                                                        type="button"
                                                        onClick={() => updateFiling(r.id, r.filing_status === "Filed" ? "Not Filed" : "Filed")}
                                                        className={classJoin("inline-flex items-center gap-1 rounded px-2 py-1 text-[10px] font-semibold border hover:shadow-sm transition-shadow", FILING_TONE[r.filing_status])}
                                                        data-testid={`gst-toggle-${r.id}`}
                                                        title={r.filing_status === "Filed" ? `Filed on ${formatDate(r.filing_date)}` : "Click to mark as Filed"}
                                                    >
                                                        {r.filing_status === "Filed" ? <CheckCircle size={11} weight="fill" /> : <Hourglass size={11} weight="fill" />}
                                                        {r.filing_status}
                                                    </button>
                                                </RoleGuard>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot className="bg-slate-50 border-t border-slate-200">
                                    <tr className="font-semibold">
                                        <td colSpan={4} className="px-4 py-3 text-right reedos-eyebrow">Total</td>
                                        <td className="px-4 py-3 text-right reedos-data">{formatINR(data.totals.taxable_value)}</td>
                                        <td className="px-4 py-3 text-right reedos-data">{formatINR(data.totals.cgst)}</td>
                                        <td className="px-4 py-3 text-right reedos-data">{formatINR(data.totals.sgst)}</td>
                                        <td className="px-4 py-3 text-right reedos-data">{formatINR(data.totals.igst)}</td>
                                        <td className="px-4 py-3 text-right reedos-data text-indigo-700 font-bold">{formatINR(data.totals.total_tax)}</td>
                                        <td colSpan={2}></td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Bulk dialog */}
            <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-display flex items-center gap-2">
                            <ListChecks size={18} className="text-slate-700" /> Bulk filing update
                        </DialogTitle>
                        <DialogDescription>{selected.size} invoice{selected.size !== 1 ? "s" : ""} selected.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label className="text-xs font-semibold">Action</Label>
                            <select value={bulkAction} onChange={(e) => setBulkAction(e.target.value)} className="mt-1.5 w-full h-10 px-3 text-sm border border-slate-200 rounded-md bg-white" data-testid="gst-bulk-action">
                                <option value="Filed">Mark as Filed</option>
                                <option value="Not Filed">Mark as Not Filed</option>
                            </select>
                        </div>
                        {bulkAction === "Filed" && (
                            <div>
                                <Label className="text-xs font-semibold">Filing date</Label>
                                <Input type="date" value={bulkDate} onChange={(e) => setBulkDate(e.target.value)} className="mt-1.5" data-testid="gst-bulk-date" />
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setBulkOpen(false)}>Cancel</Button>
                        <Button onClick={bulkApply} disabled={bulkBusy} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="gst-bulk-confirm">
                            {bulkBusy ? "Applying…" : `Apply to ${selected.size}`}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function Kpi({ label, value, sub, icon, tone = "slate", testid }) {
    const TONE = { slate: "text-slate-900", emerald: "text-emerald-700", rose: "text-rose-700", amber: "text-amber-700", indigo: "text-indigo-700" };
    return (
        <div className="reedos-card p-4" data-testid={testid}>
            <div className="reedos-eyebrow flex items-center gap-1.5">{icon} {label}</div>
            <div className={classJoin("font-display font-bold reedos-data text-2xl mt-1 leading-none", TONE[tone])}>{value}</div>
            {sub && <div className="text-[11px] text-slate-500 mt-1">{sub}</div>}
        </div>
    );
}

function FilterSelect({ label, value, onChange, options, testid }) {
    return (
        <div>
            <Label className="text-[10px] font-semibold text-slate-500 uppercase">{label}</Label>
            <select value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full h-8 px-2 text-xs border border-slate-200 rounded bg-white" data-testid={testid}>
                {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
        </div>
    );
}
